import { ErrorMessage } from '@hookform/error-message'
import { yupResolver } from '@hookform/resolvers/yup'
import { AxiosError } from 'axios'
import Button from 'components/button'
import PasswordRecovery from 'components/form-steps/password-recovery'
import Input from 'components/input'
import Password from 'components/password'
import { LoaderContext } from 'contexts/loader-context'
import { AuthError } from 'firebase/auth'
import { MouseEventHandler, useContext, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocalStorage } from 'react-use'
import formServices from 'services/form-builder-services'
import ProfileService from 'services/profile-services'
import { Checkbox, DivCheckbox, Label, PCheckbox } from 'theme/theme'
import { TextRecovery, TitleRecovery } from 'views/login/text.style'

function EmailPassword({
  state,
  onFormSubmitEndPoint,
  isSignUp,
  mailSent,
  setMailSent,
  prevStep,
  setError,
  htmlSteps
}: any) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setError: setErrorForm
  } = useForm({ resolver: yupResolver(formServices.formGenerateSchema(['email', 'password', 'referral'])) })
  const { setLoad } = useContext(LoaderContext)
  const [termsOfUse, setTermsOfUse] = useState(false)
  const [subscribe, setSubscribe] = useState(false)
  const [referralCode] = useLocalStorage('referralCode', '')

  const handleTermsOfUse = () => {
    if (!termsOfUse) setErrorForm('termsOfUse', null)
    gtag('event', 'Click', {
      event_category: GTM_EVENT_CATEGORY,
      event_label: 'MODIFICAR INFORMACION - Acepto terminos y condiciones de privacidad',
      value: '10'
    })
    setTermsOfUse((state) => !state)
  }

  const handleSubscribe = () => {
    gtag('event', 'Click', {
      event_category: GTM_EVENT_CATEGORY,
      event_label: 'MODIFICAR INFORMACION - Acepto recibir novedades',
      value: '10'
    })
    setSubscribe((state) => !state)
  }

  const handleSubmitValidate = async (data: any) => {
    if (isSignUp && !termsOfUse) {
      return setErrorForm('termsOfUse', { type: 'required', message: 'Debes aceptar los términos y condiciones' })
    }

    try {
      setLoad(true)
      if (data.referral) await ProfileService.validateReferralCode(data.referral)
      await onFormSubmitEndPoint({ ...data, subscribe, termsOfUse })
    } catch (error) {
      const typedError = error as AuthError
      if (typedError.code) {
        gtag('event', 'Click', {
          event_category: GTM_EVENT_CATEGORY,
          event_label: 'SINGUP - Referral Code existente',
          value: '10'
        })
        setError(typedError.code)
      } else if ((error as AxiosError).isAxiosError) {
        return setError((error as AxiosError).response?.data.code ?? 9999)
      }
    } finally {
      setLoad(false)
    }
  }

  const recoverPassword: MouseEventHandler<HTMLButtonElement> = async (event) => {
    event.preventDefault()
    if (!getValues('email')) {
      return alert('Debes ingresar un correo electrónico')
    }
    setLoad(true)
    try {
      const response = await ProfileService.recoverPassword({ email: mailSent || getValues('email') })
      if (response.status === 200) setMailSent(mailSent || getValues('email'))
    } catch (err) {
      const typedError = err as AxiosError
      const { code } = typedError.response?.data || {}
      setError(code)
    } finally {
      setLoad(false)
    }
  }

  if (mailSent) {
    return (
      <article className="flex flex-col flex-1 md:pt-10">
        <section className="flex h-full justify-between flex-col gap-4 mx-7 mb-7 text-center">
          <div className="flex flex-1 flex-col gap-4 items-center justify-center">
            <TitleRecovery className="text-center text-5xl font-bold uppercase">¿Olvidaste tu contraseña?</TitleRecovery>
            <TextRecovery className="text-center text-base font-normal uppercase saira">
              Ya te enviamos el mail con las instrucciones para resetear tu contraseña. Revisa tus correos para poder
              volver a ingresar.
            </TextRecovery>
            <div>
              <TextRecovery className="text-center text-base uppercase saira font-bold">¿No te llegó ningún mail?</TextRecovery>
              <TextRecovery className="text-center text-base font-normal uppercase saira">Intentémoslo de nuevo.</TextRecovery>
            </div>
            <PasswordRecovery shouldEnable={!isSignUp} recoverPassword={recoverPassword} mailSent={mailSent} />
          </div>
          <Button
            onClick={() => {
              gtag('event', 'Click', {
                event_category: GTM_EVENT_CATEGORY,
                event_label: 'MODIFICAR INFORMACION - Volver atras',
                value: '10'
              })
              setMailSent(null)
            }}
          >
            <p className="uppercase">VOLVER</p>
          </Button>
        </section>
      </article>
    )
  }

  return (
    <div className="flex flex-col flex-1 md:pt-10">
      <div className="flex flex-row items-center justify-around w-full mb-4">{htmlSteps}</div>
      <form
        className="flex h-full justify-between flex-col gap-4 mx-7 mb-7"
        onSubmit={handleSubmit(handleSubmitValidate)}
        noValidate
      >
        <div className="flex-1 flex flex-col gap-4">
          <div className="gap-2 flex flex-col">
            <Label className="text-start text-2xl font-bold uppercase">Correo electrónico</Label>
            <Input
              className="text-center saira font-bold text-sm"
              type="text"
              placeholder="roberto@gmail.com"
              name="email"
              defaultValue={state?.email}
              {...register('email')}
            />
            <ErrorMessage errors={errors} name="email" />
          </div>
          <div className="gap-2 flex flex-col">
            <Label className="text-start text-2xl font-bold uppercase">Contraseña</Label>
            <Password
              className="text-center saira font-bold text-sm"
              type="password"
              placeholder="***********"
              name="password"
              defaultValue={state?.password}
              {...register('password')}
            />
            <ErrorMessage errors={errors} name="password" />
          </div>
          <PasswordRecovery shouldEnable={!isSignUp} recoverPassword={recoverPassword} mailSent={mailSent} />
          {isSignUp && THEME_CONFIG.allowReferral && (
            <div className="gap-2 flex flex-col">
              <Label className="text-start text-xl font-medium uppercase">CÓDIGO REFERIDO (OPCIONAL)</Label>
              <Input
                className="text-center saira font-bold text-sm"
                type="text"
                placeholder="Ingresa código referido"
                name="referral"
                defaultValue={referralCode as string}
                {...register('referral')}
              />
              <ErrorMessage errors={errors} name="referral" />
            </div>
          )}
        </div>
        {isSignUp && (
          <>
            <DivCheckbox className="gap-4 flex items-center cursor-pointer" onClick={handleTermsOfUse}>
              <Checkbox active={termsOfUse} className={`h-6 w-6 flex items-center justify-center`} id="termsOfUse" />
              <PCheckbox className="dinPro">Acepto los <a href={`${process.env.PUBLIC_URL}/terms-and-conditions`} rel="noreferrer" target="_blank" className="underline">Términos y Condiciones</a> y el <a href={`${process.env.PUBLIC_URL}/privacy-politics`} rel="noreferrer" target="_blank" className="underline">Aviso de privacidad</a>.</PCheckbox>
            </DivCheckbox>
            <ErrorMessage errors={errors} name="termsOfUse" />
            <DivCheckbox className="gap-4 flex items-center cursor-pointer" onClick={handleSubscribe}>
              <Checkbox className={`h-6 w-6 flex items-center justify-center`} id="suscribe" active={subscribe} />
              <PCheckbox className="dinPro">Quiero recibir novedades.</PCheckbox>
            </DivCheckbox>
            <ErrorMessage errors={errors} name="subscribe" />
          </>
        )}
        <Button>
          <p className="uppercase">{isSignUp ? 'TERMINAR REGISTRO' : 'INICIAR SESION'}</p>
        </Button>
        {isSignUp && (
          <button
            type="button"
            className="cursor-pointer"
            onClick={() => {
              gtag('event', 'Click', {
                event_category: GTM_EVENT_CATEGORY,
                event_label: 'MODIFICAR INFORMACION - Volver atras',
                value: '10'
              })
              prevStep()
            }}
          >
            <p className={`din text-center mt-3 hover:text-red-100 uppercase underline`} style={{ fontSize: 10 }}>
              VOLVER
            </p>
          </button>
        )}
      </form>
    </div >
  )
}

export default EmailPassword
